import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Eye, Clock, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { differenceInDays } from "date-fns";
import { cn } from "@/lib/utils";
import { useDocumentPreview } from "@/contexts/DocumentPreviewContext";
import type { Tables } from "@/integrations/supabase/types";

interface ActionDetailHeaderProps {
  document: {
    id: string;
    title: string;
    doc_type: string | null;
    expires_at: string | null;
    file_path: string | null;
  };
}

export function ActionDetailHeader({ document }: ActionDetailHeaderProps) {
  const navigate = useNavigate();
  const { openDocument } = useDocumentPreview();
  
  const daysUntilExpiry = document.expires_at 
    ? differenceInDays(new Date(document.expires_at), new Date())
    : null;
    
  const isExpired = daysUntilExpiry !== null && daysUntilExpiry < 0;
  const isUrgent = daysUntilExpiry !== null && daysUntilExpiry <= 30 && !isExpired;

  const formatDeadline = () => {
    if (daysUntilExpiry === null) return null;
    if (daysUntilExpiry < 0) return `Expiré depuis ${Math.abs(daysUntilExpiry)} jours`;
    if (daysUntilExpiry === 0) return "Expire aujourd'hui";
    if (daysUntilExpiry === 1) return "Expire demain";
    return `Expire dans ${daysUntilExpiry} jours`;
  };

  const handleViewClick = async () => {
    // Fetch complete document from database for full metadata
    const { data: doc } = await supabase
      .from("documents")
      .select("*")
      .eq("id", document.id)
      .single();
    
    if (doc) {
      openDocument(doc);
    }
  };

  return (
    <div className="space-y-4">
      {/* Navigation */}
      <div className="flex items-center">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/actions')}
          className="gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour aux actions
        </Button>
      </div>
      
      {/* Document summary card */}
      <Card className={cn(
        "overflow-hidden",
        isExpired && "border-destructive/50 bg-destructive/5",
        isUrgent && !isExpired && "border-warning/50 bg-warning/5"
      )}>
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            {/* Status indicator */}
            <div className={cn(
              "w-12 h-12 rounded-xl flex items-center justify-center shrink-0",
              isExpired ? "bg-destructive/10" : isUrgent ? "bg-warning/10" : "bg-primary/10"
            )}>
              {isExpired ? (
                <AlertTriangle className="w-6 h-6 text-destructive" />
              ) : (
                <Clock className={cn(
                  "w-6 h-6",
                  isUrgent ? "text-warning" : "text-primary"
                )} />
              )}
            </div>
            
            {/* Info */}
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-semibold text-foreground truncate">
                {document.title}
              </h1>
              
              {formatDeadline() && (
                <Badge 
                  variant="outline"
                  className={cn(
                    "mt-1",
                    isExpired && "border-destructive text-destructive",
                    isUrgent && !isExpired && "border-warning text-warning"
                  )}
                >
                  <Clock className="w-3 h-3 mr-1" />
                  {formatDeadline()}
                </Badge>
              )}
            </div>
            
            {/* View button - opens preview sheet */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleViewClick}
            >
              <Eye className="w-4 h-4 mr-2" />
              Voir
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
